import { useState, useMemo } from 'react'
import { X, Plus, Loader2, AlertTriangle, Check, Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useCreateInstance } from '@/hooks/useInstances'
import { getLocalName } from '@/lib/ontology/namespaces'

// Validation rules for different entity types
interface ValidationRule {
  minLength: number
  patterns?: RegExp[]
  antiPatterns?: RegExp[]
  hints: string[]
}

const ENTITY_VALIDATION_RULES: Record<string, ValidationRule> = {
  CustomerSegment: {
    minLength: 10,
    antiPatterns: [/^everyone$/i, /^all\s+/i, /^people$/i, /^users$/i, /^customers$/i],
    hints: [
      'Be specific: "Small restaurant owners in urban areas" not "restaurants"',
      'Include demographics, behaviors, or industry',
      'Think about who has the problem most urgently',
    ],
  },
  Persona: {
    minLength: 5,
    hints: [
      'Give them a name like "Marketing Manager Maria"',
      'Personas should represent a real person, not a category',
    ],
  },
  CustomerJob: {
    minLength: 15,
    patterns: [/\b(want|need|trying|manage|find|get|make|save|grow|reduce)\b/i],
    hints: [
      'Use "verb + object" format: "Manage inventory efficiently"',
      'Focus on what they\'re trying to accomplish, not how',
    ],
  },
  CustomerPain: {
    minLength: 15,
    antiPatterns: [/^it\'s\s+hard/i, /^difficult$/i, /^frustrating$/i],
    hints: [
      'Describe the specific frustration or obstacle',
      'Include the impact: time wasted, money lost, stress caused',
    ],
  },
  CustomerGain: {
    minLength: 15,
    antiPatterns: [/^save\s+time$/i, /^save\s+money$/i, /^be\s+happy$/i],
    hints: [
      'Be specific about the desired outcome',
      'Quantify if possible: "Reduce processing time by 50%"',
    ],
  },
  Competitor: {
    minLength: 3,
    antiPatterns: [/^none$/i, /^n\/a$/i],
    hints: [
      'Include direct competitors, alternatives, and "doing nothing"',
      'Consider what customers use today to solve this problem',
    ],
  },
  ValueProposition: {
    minLength: 20,
    patterns: [/\b(help|enable|allow|save|reduce|increase|improve)\b/i],
    hints: [
      'Format: "We help [segment] to [gain] by [how]"',
      'Focus on the unique value you provide',
    ],
  },
  Product: {
    minLength: 5,
    hints: [
      'Give a clear, descriptive name',
      'Consider what it does, not just what it is',
    ],
  },
  Feature: {
    minLength: 10,
    patterns: [/\b(ability|allows|enables|lets|provides)\b/i],
    hints: [
      'Describe the capability, not just a keyword',
      'Connect to customer jobs or pains',
    ],
  },
  RevenueStream: {
    minLength: 10,
    patterns: [/\b(subscription|per|monthly|annually|fee|commission|usage)\b/i],
    hints: [
      'Include pricing model: subscription, per-use, freemium',
      'Consider who pays and for what',
    ],
  },
}

type SpecificityLevel = 'low' | 'medium' | 'high'

interface ValidationResult {
  level: SpecificityLevel
  issues: string[]
  suggestions: string[]
}

function validateInput(value: string, classUri: string): ValidationResult {
  const localName = getLocalName(classUri)
  const rules = ENTITY_VALIDATION_RULES[localName]

  const issues: string[] = []
  const suggestions: string[] = []

  if (!rules) {
    // Default validation
    if (value.length < 5) {
      return { level: 'low', issues: ['Name is too short'], suggestions: ['Add more detail'] }
    }
    if (value.length < 15) {
      return { level: 'medium', issues: [], suggestions: ['Consider adding more specific details'] }
    }
    return { level: 'high', issues: [], suggestions: [] }
  }

  // Check minimum length
  if (value.length < rules.minLength) {
    issues.push(`Too short (minimum ${rules.minLength} characters)`)
    suggestions.push(rules.hints[0])
    return { level: 'low', issues, suggestions }
  }

  // Check anti-patterns (common vague terms)
  if (rules.antiPatterns) {
    for (const pattern of rules.antiPatterns) {
      if (pattern.test(value)) {
        issues.push('Too generic or vague')
        suggestions.push(...rules.hints.slice(0, 2))
        return { level: 'low', issues, suggestions }
      }
    }
  }

  // Check for desired patterns
  let hasPattern = true
  if (rules.patterns) {
    hasPattern = rules.patterns.some(p => p.test(value))
    if (!hasPattern) {
      suggestions.push(rules.hints[0])
    }
  }

  // Determine level
  if (issues.length > 0) {
    return { level: 'low', issues, suggestions }
  }

  if (!hasPattern || value.length < rules.minLength * 1.5) {
    return { level: 'medium', issues, suggestions: rules.hints.slice(0, 1) }
  }

  return { level: 'high', issues: [], suggestions: [] }
}

interface CreateInstanceDialogProps {
  classUri: string
  classLabel: string
  color?: string
  bgColor?: string
  onClose: () => void
  onCreated?: (instanceUri: string, label: string) => void
}

export function CreateInstanceDialog({
  classUri,
  classLabel,
  color = '#847561',
  bgColor,
  onClose,
  onCreated,
}: CreateInstanceDialogProps) {
  const [label, setLabel] = useState('')
  const createInstance = useCreateInstance()

  const validation = useMemo(() => validateInput(label.trim(), classUri), [label, classUri])

  const levelColor = {
    low: '#e57373',
    medium: '#ffb74d',
    high: '#81c784',
  }

  const levelLabel = {
    low: 'Needs More Detail',
    medium: 'Could Be More Specific',
    high: 'Good Specificity',
  }

  const handleCreate = async () => {
    if (!label.trim()) return

    try {
      const instanceUri = await createInstance.mutateAsync({
        typeUri: classUri,
        label: label.trim(),
        properties: {},
      })
      onCreated?.(instanceUri, label.trim())
      // Defensive check - onClose should always be a function per interface
      // but verify to prevent runtime errors during async operations
      if (typeof onClose === 'function') {
        onClose()
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create instance:', error)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={(e) => e.target === e.currentTarget && typeof onClose === 'function' && onClose()}
    >
      <Card style={{ width: '450px', maxWidth: '90vw' }}>
        <CardHeader style={{ borderBottom: '1px solid var(--color-border)', padding: '16px', backgroundColor: bgColor }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Plus style={{ width: '18px', height: '18px', color: 'white' }} />
              </div>
              <div>
                <CardTitle style={{ fontSize: '14px', color }}>
                  New {classLabel}
                </CardTitle>
                <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  {getLocalName(classUri)}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => typeof onClose === 'function' && onClose()}>
              <X style={{ width: '16px', height: '16px' }} />
            </Button>
          </div>
        </CardHeader>

        <CardContent style={{ padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="instance-label"
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                textTransform: 'uppercase',
                marginBottom: '8px',
              }}
            >
              Name / Description
            </label>
            <textarea
              id="instance-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleCreate()}
              placeholder={`Describe this ${classLabel.toLowerCase()}...`}
              autoFocus
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                border: `1px solid ${label.trim() ? levelColor[validation.level] + '80' : color + '40'}`,
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.15s',
                resize: 'vertical',
                minHeight: '80px',
              }}
            />
          </div>

          {/* Specificity Indicator */}
          {label.trim().length > 0 && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: levelColor[validation.level] + '15',
                border: `1px solid ${levelColor[validation.level]}40`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: validation.issues.length > 0 || validation.suggestions.length > 0 ? '10px' : 0 }}>
                {validation.level === 'high' ? (
                  <Check style={{ width: '16px', height: '16px', color: levelColor.high }} />
                ) : validation.level === 'low' ? (
                  <AlertTriangle style={{ width: '16px', height: '16px', color: levelColor.low }} />
                ) : (
                  <Info style={{ width: '16px', height: '16px', color: levelColor.medium }} />
                )}
                <span style={{ fontSize: '12px', fontWeight: 600, color: levelColor[validation.level] }}>
                  {levelLabel[validation.level]}
                </span>
                {/* Progress bar */}
                <div style={{ flex: 1, height: '6px', backgroundColor: '#e0e0e0', borderRadius: '3px', marginLeft: '8px' }}>
                  <div
                    style={{
                      width: validation.level === 'high' ? '100%' : validation.level === 'medium' ? '60%' : '30%',
                      height: '100%',
                      backgroundColor: levelColor[validation.level],
                      borderRadius: '3px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              {/* Issues */}
              {validation.issues.length > 0 && (
                <ul style={{ margin: '0 0 8px 0', paddingLeft: '24px', fontSize: '11px', color: levelColor.low }}>
                  {validation.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              )}

              {/* Suggestions */}
              {validation.suggestions.length > 0 && (
                <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                  <strong>Tip:</strong> {validation.suggestions[0]}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => typeof onClose === 'function' && onClose()}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!label.trim() || createInstance.isPending}
              style={{ backgroundColor: color, color: 'white' }}
            >
              {createInstance.isPending ? (
                <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <Plus style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                  Create
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
